import React, { useEffect, useRef, useState } from "react";
import { Camera, Loader2, Check } from "lucide-react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Lets a signed-in user upload a photo OR pick a ready-made gospel avatar.
export default function AvatarPicker({ compact = false }) {
  const { user, setUser } = useAuth();
  const [presets, setPresets] = useState([]);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);

  useEffect(() => { api.get("/avatars/presets").then((r) => setPresets(r.data.presets || [])).catch(() => {}); }, []);

  const onUpload = async (file) => {
    if (!file) return;
    setBusy("upload");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/auth/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser(data.user);
      toast.success("Photo updated 🙌");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy("");
  };

  const pickPreset = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post("/auth/avatar/preset", { preset_id: id });
      setUser(data.user);
      toast.success("Avatar updated 🙌");
    } catch (e) { toast.error("Could not set avatar"); }
    setBusy("");
  };

  return (
    <div data-testid="avatar-picker">
      <div className="flex items-center gap-4">
        <div className="relative w-20 h-20 rounded-full overflow-hidden bg-[#0C1338] border border-white/10 flex items-center justify-center shrink-0">
          {user?.has_avatar
            ? <img src={`${API}/users/${user.id}/avatar?v=${user.avatar_url}${busy ? "" : ""}`} key={busy} alt="" className="w-full h-full object-cover" data-testid="avatar-img" />
            : <Camera className="w-8 h-8 text-zinc-600" />}
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="avatar-file" onChange={(e) => onUpload(e.target.files[0])} />
          <button onClick={() => fileRef.current?.click()} disabled={busy === "upload"} data-testid="avatar-upload" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-sm font-semibold transition-colors disabled:opacity-60">
            {busy === "upload" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} {user?.has_avatar ? "Change photo" : "Upload photo"}
          </button>
          <p className="text-xs text-zinc-500 mt-1.5">JPG or PNG, up to 5MB — or pick a gospel avatar below.</p>
        </div>
      </div>

      <div className="mt-4">
        <p className="overline text-zinc-400 mb-2">Gospel avatars</p>
        <div className="flex flex-wrap gap-3" data-testid="avatar-presets">
          {presets.map((p) => (
            <button
              key={p.id}
              onClick={() => pickPreset(p.id)}
              disabled={!!busy}
              data-testid={`avatar-preset-${p.id}`}
              className="relative w-14 h-14 rounded-full overflow-hidden border-2 border-white/10 hover:border-[#E6B34A] transition-colors disabled:opacity-60"
            >
              <img src={p.url} alt={p.id} className="w-full h-full object-cover" />
              {busy === p.id && <span className="absolute inset-0 bg-black/50 flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-white" /></span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
